import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type FormEvent,
  type ReactNode,
} from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { io } from "socket.io-client";
import type { Html5Qrcode } from "html5-qrcode";
import { api, apiBlob, post, getAccessToken } from "./api";
import { useAuth } from "./auth";
import { can } from "./permissions";
import "./production.css";
import {CloudLabelPrint} from './printing';
import {PrintingAreaPolicySettings} from './printing-policy';
const n = (value: any) => Number(value || 0).toLocaleString("vi-VN");
const time = (value: string) =>
  value
    ? new Date(value)
        .toLocaleString("en-GB", {
          timeZone: "Asia/Ho_Chi_Minh",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        })
        .replace(",", "")
    : "Chưa báo cáo";
const states: Record<string, string> = {
  DRAFT: "Nháp",
  RELEASED: "Đã phát hành",
  PLANNED: "Chờ công đoạn trước",
  ASSIGNED: "Chờ nhận/chạy",
  IN_PROGRESS: "Đang làm",
  PAUSED: "Chờ tiếp quản ca",
  WAITING_SUPPORT: "Chờ hỗ trợ",
  COMPLETED: "Hoàn thành",
  QUEUED: "Chờ máy",
  INTERRUPTED: "Tạm ngưng vì lệnh gấp",
};
function useData<T>(path: string, initial: T) {
  const [data, setData] = useState<T>(initial),
    [error, setError] = useState("");
  const current = useRef(0);
  const load = useCallback(async () => {
    const sequence = ++current.current;
    try {
      const value = await api<T>(path);
      if (sequence === current.current) {
        setData(value);
        setError("");
      }
    } catch (e) {
      if (sequence === current.current) setError((e as Error).message);
    }
  }, [path]);
  useEffect(() => {
    void load();
    const socket = io({
      auth: { token: getAccessToken() },
      transports: ["polling", "websocket"],
    });
    socket.io.on("reconnect_attempt", () => {
      socket.auth = { token: getAccessToken() };
    });
    const refresh = () => {
      if (document.visibilityState === "visible") void load();
    };
    for (const name of [
      "operation.updated",
      "order.updated",
      "warehouse.updated",
      "notification.created",
      "machine.session.started",
      "machine.session.ended",
    ])
      socket.on(name, refresh);
    const timer = setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    return () => {
      current.current++;
      clearInterval(timer);
      socket.disconnect();
      window.removeEventListener("focus", refresh);
    };
  }, [load]);
  return { data, load, error };
}
function useAction(refresh: () => Promise<unknown>) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const locked = useRef(false);
  const run = async (action: () => Promise<unknown>) => {
    if (locked.current) return false;
    locked.current = true;
    setBusy(true);
    setError("");
    try {
      await action();
      await refresh();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  return { busy, error, run };
}
function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="ph-panel">
      <h2>{title}</h2>
      {children}
    </section>
  );
}
function ErrorBox({ message }: { message: string }) {
  return message ? (
    <p className="error" role="alert">
      {message}
    </p>
  ) : null;
}
function Meter({ done, total, unit }: { done: any; total: any; unit: string }) {
  const value =
    Number(total) > 0 ? Math.min(100, (Number(done) / Number(total)) * 100) : 0;
  return (
    <div className="ph-meter">
      <div>
        <b>
          {n(done)} / {n(total)} {unit}
        </b>
        <span>{Math.round(value)}%</span>
      </div>
      <progress value={value} max={100} />
    </div>
  );
}
type Picker = ComponentType<{
  value: string;
  onChange: (value: string) => void;
}>;
export function ProductionWorkspace({
  DeadlinePicker,
}: {
  DeadlinePicker: Picker;
}) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tabs = [
    ["orders", "Lệnh & điều phối", "orders.view"],
    ["stock", "Ba kho & truy vết", "warehouse.view"],
    ["jobs", "Công việc sản xuất", "operations.work"],
    ["tasks", "Nhập kho / giao hàng", "tasks.work"],
    ["documents", "Phiếu xuất & Excel", "warehouse.dispatch"],
    ["stages", "Công đoạn & máy", "stages.view"],
    ["settings", "Cấu hình công ty", "settings.view"],
  ].filter((t) => can(user, t[2]!));
  const preferred =
    can(user, "orders.create") || can(user, "operations.assign")
      ? "orders"
      : can(user, "operations.work")
        ? "jobs"
        : can(user, "tasks.work")
          ? "tasks"
          : tabs[0]?.[0];
  const tab = tabs.some((t) => t[0] === params.get("tab"))
    ? params.get("tab")!
    : preferred;
  return (
    <div className="page ph-workspace">
      <div className="page-title">
        <div>
          <span className="eyebrow">SẢN XUẤT → KHO → GIAO HÀNG</span>
          <h1>Sản xuất & kho</h1>
        </div>
        <NavLink className="secondary" to="/admin/materials">
          Danh mục vật tư / công đoạn
        </NavLink>
      </div>
      <nav className="ph-tabs" aria-label="Nghiệp vụ">
        {tabs.map((t) => (
          <button
            key={t[0]}
            className={tab === t[0] ? "primary" : "secondary"}
            onClick={() => setParams({ tab: t[0]! })}
          >
            {t[1]}
          </button>
        ))}
      </nav>
      {tab === "orders" && (
        <OrdersV2
          DeadlinePicker={DeadlinePicker}
          selectedId={params.get("order")}
        />
      )}
      {tab === "stock" && <Stock />}
      {tab === "jobs" && <Jobs selectedId={params.get("job")} />}
      {tab === "tasks" && <Tasks />}
      {tab === "documents" && <Documents />}
      {tab === "stages" && <Stages />}
      {tab === "settings" && <Settings />}
      {!tab && (
        <p>Bạn chưa được cấp quyền nghiệp vụ. Liên hệ quản trị để cấp quyền.</p>
      )}
    </div>
  );
}
function OrdersV2({
  DeadlinePicker,
  selectedId,
}: {
  DeadlinePicker: Picker;
  selectedId: string | null;
}) {
  const { user } = useAuth();
  const orders = useData<any[]>("/integrated/orders", []);
  const catalogs = useData<any[]>("/dispatchers", []);
  const [creating, setCreating] = useState(false),
    [selected, setSelected] = useState(selectedId || ""),
    [deadline, setDeadline] = useState("");
  const [lines, setLines] = useState([
    { productName: "", quantity: 10000, unit: "cái" },
  ]);
  const action = useAction(orders.load);
  const create = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const d = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/.exec(deadline);
    if (!d) {
      await action.run(async () => {
        throw new Error("Deadline phải có dạng dd/mm/yyyy hh:mm.");
      });
      return;
    }
    const iso = new Date(`${d[3]}-${d[2]}-${d[1]}T${d[4]}:${d[5]}:00+07:00`);
    if (!Number.isFinite(iso.getTime())) return;
    if (
      await action.run(() =>
        post("/integrated/orders", {
          customerName: f.get("customerName"),
          dispatcherId: f.get("dispatcherId"),
          priority: Number(f.get("priority")),
          plannedEndAt: iso.toISOString(),
          notes: f.get("notes"),
          lines,
        }),
      )
    )
      setCreating(false);
  };
  return (
    <>
      <div className="ph-toolbar">
        {can(user, "orders.create") && (
          <button className="primary" onClick={() => setCreating((v) => !v)}>
            + Lệnh nhiều mặt hàng
          </button>
        )}
        <NavLink to="/admin/orders" className="secondary">
          Lệnh lịch sử
        </NavLink>
      </div>
      {creating && (
        <Panel title="Tạo lệnh · một khách hàng, nhiều mặt hàng">
          <form onSubmit={create}>
            <div className="ph-grid">
              <label>
                Khách hàng
                <input name="customerName" required maxLength={200} />
              </label>
              <label>
                Người điều phối
                <select name="dispatcherId" required>
                  <option value="">Chọn người điều phối</option>
                  {catalogs.data.map((u) => (
                    <option value={u.id} key={u.id}>
                      {u.full_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Deadline
                <DeadlinePicker value={deadline} onChange={setDeadline} />
              </label>
              <label>
                Ưu tiên
                <select name="priority">
                  <option value="3">Bình thường</option>
                  <option value="1">Gấp</option>
                </select>
              </label>
            </div>
            {lines.map((l, i) => (
              <div className="ph-line-form" key={i}>
                <label>
                  Tên hàng {i + 1}
                  <div className="ph-inline">
                    <input
                      required
                      value={l.productName}
                      onChange={(e) =>
                        setLines(
                          lines.map((v, k) =>
                            k === i ? { ...v, productName: e.target.value } : v,
                          ),
                        )
                      }
                    />
                    <button
                      type="button"
                      className="secondary"
                      title="Chèn Ø"
                      onClick={() =>
                        setLines(
                          lines.map((v, k) =>
                            k === i
                              ? { ...v, productName: v.productName + " Ø" }
                              : v,
                          ),
                        )
                      }
                    >
                      Ø
                    </button>
                  </div>
                </label>
                <label>
                  Mục tiêu
                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={l.quantity}
                    onChange={(e) =>
                      setLines(
                        lines.map((v, k) =>
                          k === i
                            ? { ...v, quantity: Number(e.target.value) }
                            : v,
                        ),
                      )
                    }
                    required
                  />
                </label>
                <label>
                  Đơn vị
                  <input
                    required
                    value={l.unit}
                    onChange={(e) =>
                      setLines(
                        lines.map((v, k) =>
                          k === i ? { ...v, unit: e.target.value } : v,
                        ),
                      )
                    }
                  />
                </label>
                <button
                  type="button"
                  className="secondary"
                  disabled={lines.length === 1}
                  onClick={() => setLines(lines.filter((_, k) => k !== i))}
                >
                  Bỏ
                </button>
              </div>
            ))}
            <button
              type="button"
              className="secondary"
              onClick={() =>
                setLines([
                  ...lines,
                  { productName: "", quantity: 10000, unit: "cái" },
                ])
              }
            >
              + Mặt hàng
            </button>
            <label>
              Ghi chú
              <textarea name="notes" />
            </label>
            <button className="primary" disabled={action.busy}>
              Lưu nháp · mã LSX tự sinh
            </button>
          </form>
        </Panel>
      )}
      <ErrorBox message={action.error || orders.error} />
      <div className="ph-order-list">
        {orders.data.map((order) => (
          <Panel
            key={order.id}
            title={`${order.order_code} · ${order.customer_name}`}
          >
            <div className="ph-toolbar">
              <span>
                {states[order.status] || order.status} · Điều phối:{" "}
                <b>{order.dispatcher_name}</b> · Deadline{" "}
                {time(order.planned_end_at)}
              </span>
              {order.status === "DRAFT" && can(user, "orders.release") && (
                <button
                  className="primary"
                  disabled={action.busy}
                  onClick={() =>
                    action.run(() =>
                      post(`/integrated/orders/${order.id}/release`),
                    )
                  }
                >
                  Phát hành
                </button>
              )}
              {can(user, "operations.assign") &&
                !["DRAFT", "CANCELLED", "COMPLETED"].includes(order.status) && (
                  <button
                    className="secondary"
                    onClick={() =>
                      setSelected(selected === order.id ? "" : order.id)
                    }
                  >
                    Điều phối công đoạn
                  </button>
                )}
              {can(user, "operations.assign") &&
                !["DRAFT", "CANCELLED", "COMPLETED"].includes(order.status) &&
                order.lines.some(
                  (line: any) => Number(line.produced) < Number(line.quantity),
                ) && (
                  <button
                    className="secondary"
                    disabled={action.busy}
                    onClick={() =>
                      action.run(() =>
                        post(`/integrated/orders/${order.id}/request-report`),
                      )
                    }
                  >
                    Yêu cầu báo cáo cả lệnh
                  </button>
                )}
              {can(user, "orders.cancel") &&
                !["CANCELLED", "COMPLETED"].includes(order.status) && (
                  <button
                    className="secondary"
                    disabled={action.busy}
                    onClick={() => {
                      const reason = prompt(
                        "Hủy lệnh chưa bắt đầu – ghi lý do (ít nhất 5 ký tự):",
                      );
                      if (reason)
                        void action.run(() =>
                          post(`/integrated/orders/${order.id}/cancel`, {
                            reason,
                          }),
                        );
                    }}
                  >
                    Hủy lệnh chưa chạy
                  </button>
                )}
            </div>
            {order.lines.map((line: any) => (
              <article className="ph-order-line" key={line.id}>
                <h3>{line.product_name}</h3>
                <Meter
                  done={line.produced}
                  total={line.quantity}
                  unit={line.unit}
                />
                <div className="ph-quantities">
                  <span>
                    Chờ nhập <b>{n(line.pending_receipt)}</b>
                  </span>
                  <span>
                    Đã nhập <b>{n(line.received)}</b>
                  </span>
                  <span>
                    Đã xuất <b>{n(line.dispatched)}</b>
                  </span>
                  <span>
                    Đã giao <b>{n(line.delivered)}</b>
                  </span>
                </div>
                <small>
                  Sản lượng báo cáo gần nhất: {time(line.last_report_at)}
                </small>
              </article>
            ))}
            {selected === order.id && (
              <Dispatch order={order} refresh={orders.load} />
            )}
          </Panel>
        ))}
      </div>
      {!orders.data.length && <p>Chưa có lệnh theo mô hình mới.</p>}
    </>
  );
}
function Dispatch({
  order,
  refresh,
}: {
  order: any;
  refresh: () => Promise<unknown>;
}) {
  const route = useData<any>(`/production-orders/${order.id}/dispatch`, null);
  const action = useAction(async () => {
    await route.load();
    await refresh();
  });
  const [definitionId, setDefinitionId] = useState(""),
    [machineId, setMachineId] = useState(""),
    [workerId, setWorkerId] = useState(""),
    [lineId, setLineId] = useState(order.lines[0]?.id || "");
  if (!route.data) return <ErrorBox message={route.error} />;
  const data = route.data,
    definition = data.definitions.find((v: any) => v.id === definitionId),
    selectedLine = order.lines.find((line: any) => line.id === lineId);
  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    if (
      await action.run(() =>
        post(`/integrated/orders/${order.id}/stages`, {
          lineId: f.get("lineId"),
          definitionId,
          machineId: machineId || null,
          workerId: workerId || null,
          plannedQuantity: Number(f.get("plannedQuantity") || 1),
          allowSelfPick: f.get("allowSelfPick") === "on",
          dependencies: f.getAll("dependencies"),
          lotCodes: f.getAll("lotCodes"),
          notes: f.get("notes"),
          ...(f.get("materialRequestId")
            ? { materialRequestId: f.get("materialRequestId") }
            : {}),
        }),
      )
    )
      form.reset();
  };
  return (
    <div className="ph-dispatch">
      <h3>Điều phối · chọn đầu vào và các công đoạn phụ thuộc</h3>
      <div className="ph-stage-list">
        {data.stages.map((op: any) => (
          <div key={op.id}>
            <b>
              {op.operation_name} ·{" "}
              {
                order.lines.find((line: any) => line.id === op.order_line_id)
                  ?.product_name
              }
            </b>
            <span>
              {op.assignee_name || "Cho phép tự nhận"} ·{" "}
              {op.machine_code || "Tự chọn/không dùng máy"} ·{" "}
              {states[op.status] || op.status}
              {op.dispatch_state === "QUEUED" ? " · Chờ máy" : ""}
            </span>
            {["PLANNED", "ASSIGNED", "ACCEPTED", "READY", "PAUSED"].includes(
              op.status,
            ) && (
              <select
                aria-label={`Người tiếp quản ${op.operation_name}`}
                disabled={action.busy}
                value={op.assigned_to_user_id || ""}
                onChange={(e) => {
                  void action.run(() =>
                    post(`/integrated/jobs/${op.id}/reassign`, {
                      workerId: e.target.value || null,
                    }),
                  );
                }}
              >
                <option value="">Cho phép tự nhận</option>
                {data.workers.map((w: any) => (
                  <option key={w.id} value={w.id}>
                    {w.full_name}
                    {w.current_machine
                      ? ` · Đang đứng ${w.current_machine}`
                      : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
        ))}
      </div>
      {data.requests
        .filter((r: any) => r.status === "OPEN" || r.status === "SUPPLYING")
        .map((r: any) => (
          <div className="ph-panel" key={r.id}>
            <b>
              {r.requested_by_name} cần nguyên liệu ·{" "}
              {r.status === "SUPPLYING" ? "Đang làm bổ sung" : "Chờ điều phối"}
            </b>
            <p>{r.note}</p>
            {r.status === "OPEN" && (
              <button
                className="secondary"
                disabled={action.busy}
                onClick={() => {
                  const lotCode = prompt("Mã cuộn phù hợp cần bổ sung:");
                  if (lotCode)
                    void action.run(() =>
                      post(`/integrated/orders/${order.id}/supply`, {
                        requestId: r.id,
                        lotCode,
                      }),
                    );
                }}
              >
                Cấp cuộn có sẵn
              </button>
            )}
          </div>
        ))}
      <form onSubmit={save}>
        <div className="ph-grid">
          <label>
            Mặt hàng
            <select
              name="lineId"
              required
              value={lineId}
              onChange={(e) => setLineId(e.target.value)}
            >
              {order.lines.map((l: any) => (
                <option key={l.id} value={l.id}>
                  {l.product_name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Công đoạn
            <select
              value={definitionId}
              onChange={(e) => {
                setDefinitionId(e.target.value);
                setMachineId("");
              }}
              required
            >
              <option value="">Chọn công đoạn</option>
              {data.definitions.map((d: any) => (
                <option key={d.id} value={d.id}>
                  {d.name} ·{" "}
                  {d.stage_kind === "FINAL_OUTPUT"
                    ? "Tạo thành phẩm"
                    : "Chuẩn bị"}
                </option>
              ))}
            </select>
          </label>
          <label>
            Máy
            <select
              value={machineId}
              onChange={(e) => setMachineId(e.target.value)}
            >
              <option value="">Công nhân tự chọn / không cần máy</option>
              {data.machines
                .filter(
                  (m: any) =>
                    !definition ||
                    definition.machine_ids?.includes(m.id) ||
                    m.operation_definition_ids?.includes(definition.id),
                )
                .map((m: any) => (
                  <option value={m.id} key={m.id}>
                    {m.code} · {m.name}
                    {m.current_orders
                      ? ` 🔴 đang làm ${m.current_orders}`
                      : " · Sẵn sàng"}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Công nhân
            <select
              value={workerId}
              onChange={(e) => setWorkerId(e.target.value)}
            >
              <option value="">Để công nhân tự nhận</option>
              {data.workers.map((u: any) => (
                <option value={u.id} key={u.id}>
                  {u.full_name} ·{" "}
                  {u.current_machine
                    ? `đang ở ${u.current_machine}`
                    : "chưa đứng máy"}
                </option>
              ))}
            </select>
          </label>
          {definition?.stage_kind === "FINAL_OUTPUT" && (
            <label>
              Sản lượng giao cho máy ({selectedLine?.unit})
              <input
                key={lineId}
                name="plannedQuantity"
                type="number"
                min="0.001"
                step="0.001"
                required
                defaultValue={
                  Math.max(
                    0,
                    Number(selectedLine?.quantity) -
                      Number(selectedLine?.produced),
                  ) || selectedLine?.quantity
                }
              />
            </label>
          )}
          <label className="ph-check">
            <input type="checkbox" name="allowSelfPick" defaultChecked />
            Cho phép tự nhận / quét mã chọn cuộn
          </label>
        </div>
        <fieldset>
          <legend>
            Phải chờ xong các công đoạn (có thể chọn nhiều để ghép)
          </legend>
          {data.stages.map((o: any) => (
            <label className="ph-check" key={o.id}>
              <input type="checkbox" name="dependencies" value={o.id} />
              {o.operation_name} ·{" "}
              {
                order.lines.find((line: any) => line.id === o.order_line_id)
                  ?.product_name
              }{" "}
              · {o.assignee_name}
            </label>
          ))}
        </fieldset>
        <fieldset>
          <legend>
            Giữ chỗ cuộn có sẵn (không chọn nếu chờ đầu ra công đoạn trước)
          </legend>
          {data.lots
            .filter(
              (l: any) =>
                Number(l.on_hand) > Number(l.reserved) &&
                !l.integrated_reserved,
            )
            .map((l: any) => (
              <label className="ph-check" key={l.id}>
                <input type="checkbox" name="lotCodes" value={l.lot_code} />
                {l.lot_code} · {l.material_name} · {n(l.on_hand)} kg
                {l.processing_spec_json?.artwork
                  ? ` · Bản in ${l.processing_spec_json.artwork}`
                  : l.processing_spec_json?.laminated
                    ? " · Đã ghép"
                    : " · Chưa in"}
                {l.weight_basis === "ESTIMATED" ? " · Cần cân thực tế" : ""}
              </label>
            ))}
        </fieldset>
        <label>
          Liên kết yêu cầu bổ sung (chọn ở bước tạo cuộn bổ sung cuối cùng)
          <select name="materialRequestId">
            <option value="">Công đoạn thông thường</option>
            {data.requests
              .filter((r: any) => r.status === "OPEN")
              .map((r: any) => (
                <option key={r.id} value={r.id}>
                  {r.requested_by_name} · {r.note}
                </option>
              ))}
          </select>
        </label>
        <label>
          Nội dung / khổ chia / bản in / lưu ý<textarea name="notes" />
        </label>
        <button className="primary" disabled={action.busy}>
          Giao công đoạn
        </button>
        <p className="ph-hint">
          Lệnh gấp không tự cắt ngang máy. Người đang chạy phải báo cáo, bàn
          giao an toàn rồi mới đổi lệnh.
        </p>
        <ErrorBox message={action.error} />
      </form>
    </div>
  );
}
function Stock() {
  const { user } = useAuth();
  const stock = useData<any>("/integrated/stock", {
      materials: [],
      rolls: [],
      finished: [],
    }),
    departments = useData<any[]>("/departments", []);
  const action = useAction(stock.load);
  const [warehouse, setWarehouse] = useState("RAW"),
    [trace, setTrace] = useState<any>(null),
    [label, setLabel] = useState<any>(null);
  const receive = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    await action.run(() =>
      post("/integrated/stock/receive", {
        materialId: f.get("materialId"),
        count: Number(f.get("count")),
        sampleWeight: Number(f.get("sampleWeight")),
        warehouse,
        departmentId: f.get("departmentId") || null,
        location: f.get("location"),
      }),
    );
  };
  const createMaterial = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    await action.run(() =>
      post("/materials", {
        code: f.get("code"),
        name: f.get("name"),
        materialKind: f.get("kind"),
        defaultUnit: "kg",
        specification: {
          width: Number(f.get("width")) || null,
          thickness: Number(f.get("thickness")) || null,
          artwork: f.get("artwork"),
          description: f.get("description"),
        },
      }),
    );
  };
  const groups = new Map<string, any[]>();
  for (const roll of stock.data.rolls.filter(
    (l: any) => l.warehouse_kind === warehouse,
  )) {
    const key =
      roll.material_id + JSON.stringify(roll.processing_spec_json || {});
    groups.set(key, [...(groups.get(key) || []), roll]);
  }
  return (
    <>
      <div className="ph-toolbar">
        {[
          ["RAW", "Kho nguyên liệu"],
          ["SEMI_FINISHED", "Kho bán thành phẩm"],
          ["FINISHED", "Kho thành phẩm"],
        ].map(([key, label]) => (
          <button
            key={key}
            className={warehouse === key ? "primary" : "secondary"}
            onClick={() => setWarehouse(key!)}
          >
            {label}
          </button>
        ))}
      </div>
      <ErrorBox message={stock.error || action.error} />
      {warehouse !== "FINISHED" && can(user, "materials.adjust") && (
        <Panel title="Nhập cuộn / túi nguyên liệu">
          <form onSubmit={receive}>
            <div className="ph-grid">
              <label>
                Loại / quy cách
                <select name="materialId" required>
                  <option value="">Chọn vật tư</option>
                  {stock.data.materials
                    .filter((m: any) => m.default_unit === "kg")
                    .map((m: any) => (
                      <option key={m.id} value={m.id}>
                        {m.name} · Khổ {m.specification_json.width || "—"} · Dày{" "}
                        {m.specification_json.thickness || "—"} ·{" "}
                        {m.specification_json.artwork || ""}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Số cuộn/túi
                <input
                  name="count"
                  type="number"
                  required
                  min="1"
                  max="500"
                  defaultValue="1"
                />
              </label>
              <label>
                Cân một cuộn mẫu (kg)
                <input
                  name="sampleWeight"
                  type="number"
                  min="0.001"
                  step="0.001"
                  required
                />
              </label>
              <label>
                Vị trí trong kho
                <input name="location" />
              </label>
              <label>
                Phạm vi xưởng (khác vị trí kho)
                <select name="departmentId">
                  <option value="">Kho chung</option>
                  {departments.data.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="ph-hint">
              Cuộn mẫu là đã cân; các cuộn còn lại ghi rõ ước lượng và có thể
              cân thực tế sau.
            </p>
            <button className="primary" disabled={action.busy}>
              Nhập kho · tự cấp mã riêng từng cuộn
            </button>
          </form>
        </Panel>
      )}
      {can(user, "materials.manage") && (
        <details className="ph-panel">
          <summary>+ Khai báo loại vật tư / quy cách mới</summary>
          <form onSubmit={createMaterial}>
            <div className="ph-grid">
              <label>
                Mã loại
                <input name="code" required />
              </label>
              <label>
                Tên
                <input name="name" required />
              </label>
              <label>
                Phân loại
                <select name="kind">
                  <option value="RAW">Nguyên liệu</option>
                  <option value="SEMI_FINISHED">Bán thành phẩm</option>
                </select>
              </label>
              <label>
                Khổ (mm)
                <input name="width" type="number" min="0" step="0.001" />
              </label>
              <label>
                Độ dày
                <input name="thickness" type="number" min="0" step="0.001" />
              </label>
              <label>
                Bản in / mẫu
                <input name="artwork" />
              </label>
              <label>
                Mô tả
                <input name="description" />
              </label>
            </div>
            <button className="primary" disabled={action.busy}>
              Lưu quy cách riêng
            </button>
          </form>
        </details>
      )}
      {warehouse !== "FINISHED" ? (
        [...groups.values()].map((rolls) => (
          <Panel
            key={
              rolls[0].material_id +
              JSON.stringify(rolls[0].processing_spec_json || {})
            }
            title={`${rolls[0].material_name} · ${n(rolls.reduce((s, l) => s + Number(l.on_hand), 0))} kg · ${rolls.length} cuộn/túi`}
          >
            <p>
              Khổ {rolls[0].specification_json.width || "—"} · Dày{" "}
              {rolls[0].specification_json.thickness || "—"} ·{" "}
              {rolls[0].processing_spec_json?.artwork ||
                rolls[0].specification_json.artwork ||
                "Chưa in"}
            </p>
            <p>
              Khả dụng:{" "}
              {n(
                rolls
                  .filter((l) => !l.reserved_for)
                  .reduce((s, l) => s + Number(l.on_hand), 0),
              )}{" "}
              kg
            </p>
            {rolls.map((l) => (
              <div className="ph-stock-row" key={l.id}>
                <div>
                  <b>{l.lot_code}</b>
                  <span>
                    {n(l.on_hand)} kg ·{" "}
                    {l.weight_basis === "ESTIMATED"
                      ? "Ước lượng"
                      : l.weight_basis === "INHERITED"
                        ? "Kế thừa cân trước"
                        : "Đã cân"}{" "}
                    · {l.location || "Chưa ghi vị trí"}
                  </span>
                  <small>
                    {l.reserved_for
                      ? `${l.issued_at ? "Đã đưa vào máy" : "Giữ chỗ"} · ${l.order_code}`
                      : "Có thể nhận"}
                  </small>
                </div>
                <button
                  className="secondary"
                  disabled={action.busy}
                  onClick={() =>
                    action.run(async () =>
                      setLabel({...await api(`/integrated/stock/${l.id}/label`),sourceId:l.id}),
                    )
                  }
                >
                  Mã / in QR
                </button>
                <button
                  className="secondary"
                  disabled={action.busy}
                  onClick={() =>
                    action.run(async () =>
                      setTrace(await api(`/integrated/trace/${l.id}`)),
                    )
                  }
                >
                  Truy vết
                </button>
                {can(user, "materials.adjust") && !l.reserved_for && (
                  <button
                    className="secondary"
                    disabled={action.busy}
                    onClick={() => {
                      const value = prompt("Cân thực tế (kg)");
                      if (value)
                        void action.run(() =>
                          post(`/integrated/stock/${l.id}/weigh`, {
                            weight: Number(value.replace(",", ".")),
                          }),
                        );
                    }}
                  >
                    Cân lại
                  </button>
                )}
              </div>
            ))}
          </Panel>
        ))
      ) : (
        <Panel title="Thành phẩm · lô vật lý riêng, không quản lý bọc">
          {stock.data.finished.map((l: any) => (
            <div className="ph-stock-row" key={l.id}>
              <div>
                <b>
                  {l.code} · {l.product_name}
                </b>
                <span>
                  {l.order_code} · {l.customer_name}
                </span>
                <span>
                  {l.status === "PENDING"
                    ? `Chờ nhập ${n(l.quantity)}`
                    : `Tồn ${n(l.on_hand)}`}{" "}
                  {l.unit}
                </span>
                <small>
                  {l.worker_name} · {l.machine_code}
                </small>
              </div>
              <button
                className="secondary"
                disabled={action.busy}
                onClick={() =>
                  action.run(async () =>
                    setLabel({...await api(`/integrated/stock/${l.id}/label`),sourceId:l.id}),
                  )
                }
              >
                Mã / in QR
              </button>
              <button
                className="secondary"
                disabled={action.busy}
                onClick={() =>
                  action.run(async () =>
                    setTrace(await api(`/integrated/trace/${l.id}`)),
                  )
                }
              >
                Truy vết
              </button>
            </div>
          ))}
        </Panel>
      )}
      {label && (
        <div className="ph-print-overlay">
          <div className="ph-print-actions">
            <CloudLabelPrint sourceId={label.sourceId}/>
            <button className="primary" onClick={() => window.print()}>
              In nhãn · chọn máy in / khổ giấy
            </button>
            <button className="secondary" onClick={() => setLabel(null)}>
              Đóng
            </button>
          </div>
          <article className="ph-print-document ph-roll-label">
            <h2>{label.code}</h2>
            <img
              src={label.qr}
              alt={`QR ${label.code}`}
              width="240"
              height="240"
            />
            <h3>{label.name}</h3>
            <p>
              {n(label.quantity)} {label.unit}
              {label.weight_basis === "ESTIMATED"
                ? " · Ước lượng, chưa cân thực tế"
                : ""}
            </p>
            {label.specification_json && (
              <p>
                Khổ {label.specification_json.width || "—"} · Dày{" "}
                {label.specification_json.thickness || "—"}
              </p>
            )}
            {label.processing_spec_json?.artwork && (
              <p>Bản in: {label.processing_spec_json.artwork}</p>
            )}
            <p>Có thể ghi tay mã phía trên nếu không in QR.</p>
          </article>
        </div>
      )}
      {trace && (
        <Panel title="Nguồn gốc cuộn / thành phẩm">
          <button className="secondary" onClick={() => setTrace(null)}>
            Đóng truy vết
          </button>
          {trace.finished && (
            <p>
              {trace.finished.product_name} · {trace.finished.worker_name} ·{" "}
              {trace.finished.machine_code}
            </p>
          )}
          {trace.ancestry.map((l: any) => (
            <div className="ph-stock-row" key={l.id}>
              <b>
                {l.lot_code} · {l.material_name}
              </b>
              <span>
                {l.original_receipt_at
                  ? `Nhập gốc ${time(l.original_receipt_at)}`
                  : "Bán thành phẩm sinh ra trong sản xuất"}
              </span>
            </div>
          ))}
          {trace.histories.map((h: any) => (
            <p key={h.id}>
              {h.actor_name} · {h.operation_name} · {h.machine_code} ·{" "}
              {time(h.created_at)}
            </p>
          ))}
          {trace.transformations.map((h: any, i: number) => (
            <p key={i}>
              {h.operation_name} · {h.worker_name} · {h.machine_code} ·{" "}
              {h.relation}
            </p>
          ))}
          {trace.documents.map((d: any) => (
            <p key={d.number}>
              {d.number} → {d.customer_name} · {n(d.quantity)}
            </p>
          ))}
        </Panel>
      )}
    </>
  );
}
function Jobs({ selectedId }: { selectedId: string | null }) {
  const jobs = useData<any[]>("/integrated/jobs", []),
    requests = useData<any[]>("/integrated/requests", []);
  const [selected, setSelected] = useState(selectedId || "");
  const { user } = useAuth();
  const action = useAction(jobs.load);
  return (
    <>
      <Panel title="Yêu cầu báo cáo đang chờ">
        {requests.data.map((r) => (
          <div className="ph-stock-row" key={r.id}>
            <div>
              <b>
                {r.order_code} · {r.operation_name}
              </b>
              <span>
                {r.is_final_output
                  ? "Báo số lượng mới chưa ghi nhận"
                  : "Xác nhận công đoạn, không nhập miếng thành phẩm"}
              </span>
            </div>
            <button
              className="primary"
              onClick={() => setSelected(r.operation_id)}
            >
              Mở để báo cáo
            </button>
          </div>
        ))}
        {!requests.data.length && <p>Không có yêu cầu đang chờ.</p>}
      </Panel>
      <ErrorBox message={jobs.error || action.error} />
      {jobs.data.map((op) => (
        <Panel key={op.id} title={`${op.order_code} · ${op.product_name}`}>
          <div className="ph-toolbar">
            <span>
              <b>{op.operation_name}</b> ·{" "}
              {op.machine_code || "Tự chọn máy/không cần máy"} ·{" "}
              {states[op.status] || op.status}{" "}
              {op.dispatch_state === "QUEUED" ? " · Chờ máy" : ""}
            </span>
            {!op.assigned_to_user_id ? (
              <button
                className="primary"
                disabled={action.busy}
                onClick={() =>
                  action.run(() => post(`/integrated/jobs/${op.id}/claim`))
                }
              >
                Nhận công đoạn
              </button>
            ) : (
              <button
                className="primary"
                onClick={() => setSelected(selected === op.id ? "" : op.id)}
              >
                Mở công việc
              </button>
            )}
          </div>
          {op.is_final_output && (
            <Meter
              done={op.completed_quantity}
              total={op.planned_quantity}
              unit={op.line_unit}
            />
          )}
        </Panel>
      ))}
      {selected && (
        <JobEditor
          id={selected}
          close={() => setSelected("")}
          refresh={async () => {
            await jobs.load();
            await requests.load();
          }}
        />
      )}
      {can(user, "sessions.work") && (
        <NavLink className="secondary" to="/scan-machine">
          Quét QR check in máy
        </NavLink>
      )}
    </>
  );
}
function RollScanner({ onCode }: { onCode: (code: string) => void }) {
  const [open, setOpen] = useState(false),
    [error, setError] = useState("");
  const qr = useRef<Html5Qrcode | null>(null);
  const id = useRef(`roll-reader-${Math.random().toString(36).slice(2)}`);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const start = async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;
        const scanner = new Html5Qrcode(id.current);
        qr.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 220 },
          (raw) => {
            if (cancelled) return;
            cancelled = true;
            onCode(raw.trim());
            setOpen(false);
          },
          () => {},
        );
        if (cancelled && scanner.isScanning) await scanner.stop();
      } catch {
        if (!cancelled)
          setError(
            "Không mở được camera; dùng mã ghi tay hoặc cấp quyền camera.",
          );
      }
    };
    void start();
    return () => {
      cancelled = true;
      const scanner = qr.current;
      qr.current = null;
      if (scanner)
        void (async () => {
          try {
            if (scanner.isScanning) await scanner.stop();
            scanner.clear();
          } catch {}
        })();
    };
  }, [open, onCode]);
  return (
    <>
      <button
        type="button"
        className="secondary"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Đóng camera" : "Quét QR cuộn"}
      </button>
      {open && <div id={id.current} />}
      <ErrorBox message={error} />
    </>
  );
}
function JobEditor({
  id,
  close,
  refresh,
}: {
  id: string;
  close: () => void;
  refresh: () => Promise<unknown>;
}) {
  const data = useData<any>(`/integrated/jobs/${id}`, null),
    materials = useData<any[]>("/materials", []);
  const action = useAction(async () => {
    await data.load();
    await refresh();
  });
  const [code, setCode] = useState(""),
    [good, setGood] = useState(""),
    [defect, setDefect] = useState(""),
    [note, setNote] = useState(""),
    [outputs, setOutputs] = useState<any[]>([]),
    [exhausted, setExhausted] = useState(false),
    [equalLength, setEqualLength] = useState(false);
  const [artwork, setArtwork] = useState("");
  const keys = useRef<Record<string, string>>({});
  const scanned = useCallback((raw: string) => {
    try {
      const url = new URL(raw);
      setCode(url.searchParams.get("code") || raw);
    } catch {
      setCode(raw);
    }
  }, []);
  const op = data.data;
  if (!op) return <ErrorBox message={data.error} />;
  const report = async (kind: string) => {
    const reportId = (keys.current[kind] ??= crypto.randomUUID());
    const payload = {
      reportId,
      goodQuantity: op.is_final_output ? Number(good || 0) : 0,
      defectQuantity:
        op.is_final_output && defect !== "" ? Number(defect) : null,
      note,
      ...(kind === "finish"
        ? {
            outputs: outputs.map((o) => ({
              ...o,
              quantity: Number(o.quantity),
            })),
            exhausted,
            equalLengthConfirmed: equalLength,
            artwork,
          }
        : {}),
    };
    if (
      await action.run(() => post(`/integrated/jobs/${id}/${kind}`, payload))
    ) {
      delete keys.current[kind];
      setGood("");
      setDefect("");
      setNote("");
      if (kind !== "report") close();
    }
  };
  return (
    <Panel title={`${op.operation_name} · ${op.product_name}`}>
      <div className="ph-toolbar">
        <button className="secondary" onClick={close}>
          Đóng công việc
        </button>
        <NavLink className="secondary" to="/scan-machine">
          Quét mã máy
        </NavLink>
      </div>
      <p>
        {op.order_code} · {op.route_notes}
      </p>
      <ErrorBox message={action.error} />
      <h3>Cuộn đang nhận</h3>
      {op.inputs.map((l: any) => (
        <p key={l.id}>
          <b>{l.lot_code}</b> · {l.material_name} · {n(l.on_hand)} kg{" "}
          {l.weight_basis === "ESTIMATED" ? "(ước lượng)" : ""}
          {l.processing_spec_json?.artwork
            ? ` · Bản in ${l.processing_spec_json.artwork}`
            : ""}
          {l.weight_basis === "ESTIMATED" && (
            <button
              className="secondary"
              disabled={action.busy}
              onClick={() => {
                const value = prompt(`Cân thực tế đầu vào ${l.lot_code} (kg):`);
                if (value)
                  void action.run(() =>
                    post(`/integrated/jobs/${id}/weigh-input`, {
                      lotId: l.id,
                      weight: Number(value.replace(",", ".")),
                    }),
                  );
              }}
            >
              Cân đầu vào một lần
            </button>
          )}
        </p>
      ))}
      {op.allow_self_pick && (
        <div className="ph-inline">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Mã QR hoặc mã ghi tay"
            aria-label="Mã cuộn"
          />
          <RollScanner onCode={scanned} />
          <button
            className="secondary"
            disabled={action.busy || !code}
            onClick={() =>
              action.run(() => post(`/integrated/jobs/${id}/bind`, { code }))
            }
          >
            Nhận cuộn
          </button>
        </div>
      )}
      {op.status !== "IN_PROGRESS" && op.status !== "WAITING_SUPPORT" && (
        <button
          className="primary"
          disabled={action.busy}
          onClick={() => action.run(() => post(`/integrated/jobs/${id}/start`))}
        >
          Tiếp nhận & bắt đầu / tiếp tục ca
        </button>
      )}
      {["IN_PROGRESS", "WAITING_SUPPORT"].includes(op.status) && (
        <>
          <div className="ph-grid">
            {op.is_final_output && (
              <label>
                Sản lượng đạt mới chưa báo ({op.line_unit})
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={good}
                  onChange={(e) => {
                    setGood(e.target.value);
                    keys.current = {};
                  }}
                  placeholder="0 nếu đã báo hết trước đó"
                />
              </label>
            )}
            {op.is_final_output && op.policy !== "OFF" && (
              <label>
                Sản lượng lỗi ({op.line_unit}){" "}
                {op.policy === "OPTIONAL" ? "(không bắt buộc)" : "(bắt buộc)"}
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={defect}
                  onChange={(e) => {
                    setDefect(e.target.value);
                    keys.current = {};
                  }}
                  placeholder="Bỏ trống = chưa ghi nhận"
                />
              </label>
            )}
            <label>
              Ghi chú
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          </div>
          <p className="ph-hint">
            {op.is_final_output
              ? "Chỉ nhập phần đạt chưa báo trước đó, không nhập lại tổng cả ca. Số trên máy do công nhân tự kiểm tra/reset."
              : "Công đoạn này chỉ báo trạng thái; kg vật tư không cộng vào tiến độ thành phẩm."}
          </p>
          <div className="ph-toolbar">
            <button
              className="primary"
              disabled={action.busy}
              onClick={() => report("report")}
            >
              Báo cáo hiện tại
            </button>
            <button
              className="secondary"
              disabled={action.busy}
              onClick={() => report("shift")}
            >
              Báo cáo & rời máy kết ca
            </button>
            <button
              className="secondary"
              disabled={action.busy}
              onClick={() => {
                const message = prompt(
                  "Mô tả thiếu nguyên liệu để gửi cho người điều phối trực tiếp:",
                );
                if (message)
                  void action.run(() =>
                    post(`/operations/${id}/support`, {
                      reason: "MATERIAL_SHORTAGE",
                      note: message,
                    }),
                  );
              }}
            >
              Báo thiếu nguyên liệu
            </button>
            <NavLink className="secondary" to="/report-issue">
              Báo sự cố máy
            </NavLink>
          </div>
          <details>
            <summary>
              Hoàn thành phần việc · xử lý vật tư đầu ra / phần dư
            </summary>
            {op.material_behavior === "PRINT" && (
              <>
                <p>
                  Chỉ áp dụng in trọn cuộn: giữ mã và khối lượng, ghi lịch sử
                  in. Nếu in một phần cần báo người điều phối, không chốt toàn
                  cuộn.
                </p>
                <label>
                  Mã / tên bản in (bắt buộc)
                  <input
                    value={artwork}
                    maxLength={200}
                    onChange={(e) => setArtwork(e.target.value)}
                    placeholder="Ví dụ: ABC – seal Ø110"
                  />
                </label>
              </>
            )}
            {op.material_behavior === "LAMINATE" && (
              <label className="ph-check">
                <input
                  type="checkbox"
                  checked={equalLength}
                  onChange={(e) => setEqualLength(e.target.checked)}
                />
                Đã xác nhận hai cuộn cùng độ dài, ghép hết không dư
              </label>
            )}
            {op.material_behavior === "FINAL" && (
              <label className="ph-check">
                <input
                  type="checkbox"
                  checked={exhausted}
                  onChange={(e) => setExhausted(e.target.checked)}
                />
                Đã dùng hết nguyên liệu (không suy ra từ đủ/dư sản lượng)
              </label>
            )}
            {["SPLIT", "LAMINATE", "FINAL"].includes(op.material_behavior) && (
              <>
                {outputs.map((o, i) => (
                  <div className="ph-line-form" key={i}>
                    <label>
                      Loại cuộn đầu ra / dư
                      <select
                        value={o.materialId}
                        onChange={(e) =>
                          setOutputs(
                            outputs.map((v, k) =>
                              k === i
                                ? { ...v, materialId: e.target.value }
                                : v,
                            ),
                          )
                        }
                      >
                        <option value="">Chọn quy cách</option>
                        {materials.data.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name} · khổ {m.specification_json.width || "—"}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Cân đầu ra (kg)
                      <input
                        type="number"
                        step="0.001"
                        min="0.001"
                        value={o.quantity}
                        onChange={(e) =>
                          setOutputs(
                            outputs.map((v, k) =>
                              k === i ? { ...v, quantity: e.target.value } : v,
                            ),
                          )
                        }
                      />
                    </label>
                    {op.material_behavior === "FINAL" && (
                      <label>
                        Phần dư của cuộn
                        <select
                          value={o.remainderOf || ""}
                          onChange={(e) =>
                            setOutputs(
                              outputs.map((v, k) =>
                                k === i
                                  ? { ...v, remainderOf: e.target.value }
                                  : v,
                              ),
                            )
                          }
                        >
                          <option value="">Chọn cuộn gốc</option>
                          {op.inputs.map((l: any) => (
                            <option key={l.id} value={l.id}>
                              {l.lot_code}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <button
                      className="secondary"
                      onClick={() =>
                        setOutputs(outputs.filter((_, k) => k !== i))
                      }
                    >
                      Bỏ
                    </button>
                  </div>
                ))}
                <button
                  className="secondary"
                  onClick={() =>
                    setOutputs([...outputs, { materialId: "", quantity: "" }])
                  }
                >
                  + Cuộn đầu ra / phần dư
                </button>
              </>
            )}
            <p>
              App cấp mã mới; xem ở kho bán thành phẩm để in QR hoặc ghi mã lên
              cuộn.
            </p>
            <button
              className="primary"
              disabled={action.busy}
              onClick={() => {
                if (
                  confirm(
                    "Chốt công đoạn và vật tư? Phần sản lượng ở trên chỉ là số mới chưa báo.",
                  )
                )
                  void report("finish");
              }}
            >
              Báo cáo & hoàn thành công đoạn
            </button>
          </details>
        </>
      )}
    </Panel>
  );
}
function Tasks() {
  const tasks = useData<any[]>("/integrated/tasks", []),
    { user } = useAuth();
  const action = useAction(tasks.load);
  return (
    <>
      <ErrorBox message={tasks.error || action.error} />
      <Panel title="Việc đang chờ · chỉ một người có thể nhận">
        {tasks.data.map((t) => (
          <div className="ph-stock-row" key={t.id}>
            <div>
              <b>
                {t.kind === "RECEIVE_FINISHED"
                  ? `Nhập thành phẩm ${t.lot_code}`
                  : `Giao hàng ${t.number}`}
              </b>
              <span>
                {t.product_name || t.customer_name} ·{" "}
                {t.order_code || t.customer_snapshot?.address}
              </span>
              {t.quantity && (
                <span>
                  {n(t.quantity)} {t.unit}
                </span>
              )}
              <small>
                {t.claimant_name
                  ? `${t.claimant_name} đang làm`
                  : "Chờ nhận việc"}
              </small>
            </div>
            {t.status === "OPEN" ? (
              <button
                className="primary"
                disabled={action.busy}
                onClick={() =>
                  action.run(() => post(`/integrated/tasks/${t.id}/claim`))
                }
              >
                Nhận việc
              </button>
            ) : (
              t.claimed_by === user?.id && (
                <button
                  className="primary"
                  disabled={action.busy}
                  onClick={() => {
                    if (
                      confirm(
                        t.kind === "RECEIVE_FINISHED"
                          ? "Xác nhận hàng đã vào kho?"
                          : "Xác nhận đã giao cho bên nhận?",
                      )
                    )
                      void action.run(() =>
                        post(`/integrated/tasks/${t.id}/finish`),
                      );
                  }}
                >
                  Xác nhận{" "}
                  {t.kind === "RECEIVE_FINISHED" ? "đã nhập kho" : "đã giao"}
                </button>
              )
            )}
          </div>
        ))}
        {!tasks.data.length && (
          <p>Không có việc đang chờ trong phạm vi của bạn.</p>
        )}
      </Panel>
    </>
  );
}
async function downloadFile(path: string, name: string) {
  const blob = await apiBlob(path, { feedback: { pending: 'Đang chuẩn bị file Excel…', success: 'Đã chuẩn bị file Excel.', detail: 'Trình duyệt sẽ tải file xuống. Kiểm tra thư mục tải về của thiết bị.' } });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function Documents() {
  const documents = useData<any[]>("/integrated/documents", []),
    stock = useData<any>("/integrated/stock", { finished: [], rolls: [] }),
    workers = useData<any[]>("/users", []);
  const { user } = useAuth();
  const action = useAction(documents.load);
  const [customer, setCustomer] = useState(""),
    [purpose, setPurpose] = useState("CUSTOMER"),
    [taxRate, setTaxRate] = useState(""),
    [date, setDate] = useState(
      new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Ho_Chi_Minh" }),
    ),
    [selection, setSelection] = useState<
      Record<string, { quantity: string; price: string }>
    >({}),
    [printNote, setPrintNote] = useState<any>(null),
    [editNote, setEditNote] = useState<any>(null),
    [revisions, setRevisions] = useState<string[]>([]);
  const candidates =
    purpose === "CUSTOMER"
      ? stock.data.finished.filter(
          (l: any) =>
            l.status === "AVAILABLE" &&
            Number(l.on_hand) > 0 &&
            (!customer || l.customer_name === customer),
        )
      : stock.data.rolls.filter(
          (l: any) => l.warehouse_kind === "RAW" && !l.reserved_for,
        );
  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    await action.run(async () => {
      await post("/integrated/documents", {
        businessDate: date,
        purpose,
        customerName: customer,
        address: f.get("address"),
        contact: f.get("contact"),
        vehicle: f.get("vehicle"),
        driverId: f.get("driverId"),
        taxRate: taxRate === "" ? null : Number(taxRate),
        lines: Object.entries(selection).map(([id, l]) => ({
          ...(purpose === "CUSTOMER"
            ? { finishedLotId: id }
            : { materialLotId: id }),
          quantity: Number(l.quantity),
          unitPrice: Number(l.price),
        })),
      });
      setSelection({});
      await stock.load();
    });
  };
  return (
    <>
      <ErrorBox message={documents.error || action.error} />
      <Panel title="Xuất kho · mỗi phiếu là một lần giao">
        <form onSubmit={save}>
          <div className="ph-grid">
            <label>
              Loại phiếu
              <select
                value={purpose}
                onChange={(e) => {
                  setPurpose(e.target.value);
                  setSelection({});
                  setCustomer("");
                  setTaxRate("");
                }}
              >
                <option value="CUSTOMER">Giao khách · kho thành phẩm</option>
                <option value="RETURN_TO_SUPPLIER">
                  Trả nhà cung cấp · kho nguyên liệu
                </option>
              </select>
            </label>
            <label>
              Khách / đơn vị nhận
              <input
                value={customer}
                onChange={(e) => {
                  setCustomer(e.target.value);
                  setSelection({});
                }}
                list="ph-customers"
                required
              />
              <datalist id="ph-customers">
                {[
                  ...new Set<string>(
                    stock.data.finished.map((l: any) => l.customer_name),
                  ),
                ].map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </label>
            <label>
              Ngày phiếu
              <input
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
            <label>
              Người giao
              <select name="driverId" required>
                <option value="">Chọn người giao</option>
                {workers.data.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Địa chỉ
              <input name="address" />
            </label>
            <label>
              Người nhận / liên hệ
              <input name="contact" />
            </label>
            <label>
              Phương tiện
              <input name="vehicle" />
            </label>
            <label>
              Thuế
              <select
                value={
                  taxRate === ""
                    ? "NONE"
                    : ["8", "10"].includes(taxRate)
                      ? taxRate
                      : "CUSTOM"
                }
                disabled={purpose === "RETURN_TO_SUPPLIER"}
                onChange={(e) =>
                  setTaxRate(
                    e.target.value === "NONE"
                      ? ""
                      : e.target.value === "CUSTOM"
                        ? "1"
                        : e.target.value,
                  )
                }
              >
                <option value="NONE">Không thuế (KHD)</option>
                <option value="8">8% (HD)</option>
                <option value="10">10% (HD)</option>
                <option value="CUSTOM">Tự nhập (HD)</option>
              </select>
            </label>
            {taxRate && !["8", "10"].includes(taxRate) && (
              <label>
                Thuế tự nhập (%)
                <input
                  type="number"
                  min="0.001"
                  max="100"
                  step="0.001"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                />
              </label>
            )}
          </div>
          <h3>Chọn lô xuất (không gộp lô vật lý)</h3>
          {candidates.map((l: any) => (
            <div className="ph-stock-row" key={l.id}>
              <label className="ph-check">
                <input
                  type="checkbox"
                  checked={!!selection[l.id]}
                  onChange={(e) =>
                    setSelection((old) => {
                      const copy = { ...old };
                      if (e.target.checked)
                        copy[l.id] = {
                          quantity: String(l.on_hand),
                          price: "0",
                        };
                      else delete copy[l.id];
                      return copy;
                    })
                  }
                />
                {l.code || l.lot_code} · {l.product_name || l.material_name} ·
                Tồn {n(l.on_hand)} {l.unit}
              </label>
              {selection[l.id] && (
                <>
                  <label>
                    Số lượng
                    <input
                      aria-label={`Số lượng ${l.code || l.lot_code}`}
                      type="number"
                      min="0.001"
                      max={l.on_hand}
                      step="0.001"
                      value={selection[l.id]?.quantity}
                      onChange={(e) =>
                        setSelection({
                          ...selection,
                          [l.id]: {
                            ...selection[l.id]!,
                            quantity: e.target.value,
                          },
                        })
                      }
                    />
                  </label>
                  <label>
                    Đơn giá
                    <input
                      aria-label={`Đơn giá ${l.code || l.lot_code}`}
                      type="number"
                      min="0"
                      value={selection[l.id]?.price}
                      onChange={(e) =>
                        setSelection({
                          ...selection,
                          [l.id]: {
                            ...selection[l.id]!,
                            price: e.target.value,
                          },
                        })
                      }
                    />
                  </label>
                </>
              )}
            </div>
          ))}
          <button
            className="primary"
            disabled={action.busy || !Object.keys(selection).length}
          >
            Phát hành & xuất kho
          </button>
        </form>
      </Panel>
      <Panel title="Lưu trữ phiếu / tải Excel theo ngày">
        <div className="ph-toolbar">
          <input
            aria-label="Ngày tải Excel"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          {["KHD", "HD"].map((kind) => (
            <button
              key={kind}
              className="secondary"
              disabled={action.busy}
              onClick={() =>
                action.run(() =>
                  downloadFile(
                    `/integrated/documents/export?date=${date}&kind=${kind}`,
                    `${date.split("-").reverse().join(".")} ${kind}.xlsx`,
                  ),
                )
              }
            >
              Tải {kind}
            </button>
          ))}
        </div>
        {documents.data.map((d) => (
          <div className="ph-stock-row" key={d.id}>
            {can(user, "warehouse.revise") && (
              <input
                aria-label={`Chọn sửa ${d.number}`}
                type="checkbox"
                checked={revisions.includes(d.id)}
                onChange={(e) =>
                  setRevisions(
                    e.target.checked
                      ? [...revisions, d.id]
                      : revisions.filter((id) => id !== d.id),
                  )
                }
              />
            )}
            <div>
              <b>
                {d.number} · {d.customer_name}
              </b>
              <span>
                {n(d.total)} đ ·{" "}
                {d.tax_rate == null ? "KHD" : `HD ${n(d.tax_rate)}%`} ·{" "}
                {d.status === "DELIVERED"
                  ? "Đã giao"
                  : d.status === "DELIVERING"
                    ? `${d.driver_name} đang giao`
                    : `Giao cho ${d.driver_name}`}
              </span>
            </div>
            <button
              className="secondary"
              onClick={() =>
                action.run(async () =>
                  setPrintNote(await api(`/integrated/documents/${d.id}`)),
                )
              }
            >
              Xem / in phiếu
            </button>
            {can(user, "warehouse.revise") && (
              <button
                className="secondary"
                disabled={action.busy}
                onClick={() =>
                  action.run(async () =>
                    setEditNote(await api(`/integrated/documents/${d.id}`)),
                  )
                }
              >
                Sửa thông tin / giá / thuế
              </button>
            )}
          </div>
        ))}
        {revisions.length > 0 && (
          <button
            className="secondary"
            disabled={action.busy}
            onClick={() => {
              const reason = prompt(
                "Lý do cập nhật thông tin công ty cho các phiếu đã chọn:",
              );
              if (reason)
                void action.run(() =>
                  post("/integrated/documents/revise", {
                    ids: revisions,
                    refreshCompany: true,
                    reason,
                  }),
                );
            }}
          >
            Áp dụng setting công ty mới cho {revisions.length} phiếu đã chọn
          </button>
        )}
      </Panel>
      {editNote && (
        <RevisionEditor
          key={editNote.id}
          note={editNote}
          close={() => setEditNote(null)}
          refresh={documents.load}
        />
      )}
      {printNote && (
        <div className="ph-print-overlay">
          <div className="ph-print-actions">
            <button className="primary" onClick={() => window.print()}>
              In · chọn máy in / khổ giấy
            </button>
            <button className="secondary" onClick={() => setPrintNote(null)}>
              Đóng
            </button>
          </div>
          <PrintVoucher note={printNote} />
        </div>
      )}
    </>
  );
}
function RevisionEditor({
  note,
  close,
  refresh,
}: {
  note: any;
  close: () => void;
  refresh: () => Promise<unknown>;
}) {
  const action = useAction(refresh);
  const [taxMode, setTaxMode] = useState("keep");
  return (
    <div
      className="ph-editor-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Điều chỉnh phiếu ${note.number}`}
    >
      <Panel title={`Điều chỉnh phiếu ${note.number}`}>
        <p>
          Không sửa số lượng hay xuất kho lần nữa. Mọi điều chỉnh lưu lịch sử
          cùng lý do.
        </p>
        <ErrorBox message={action.error} />
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const saved = await action.run(() =>
              post("/integrated/documents/revise", {
                ids: [note.id],
                refreshCompany: f.get("refreshCompany") === "on",
                reason: f.get("reason"),
                customer: {
                  address: f.get("address"),
                  contact: f.get("contact"),
                  vehicle: f.get("vehicle"),
                },
                ...(taxMode !== "keep"
                  ? {
                      taxRate:
                        taxMode === "none"
                          ? null
                          : taxMode === "custom"
                            ? Number(f.get("customTax"))
                            : Number(taxMode),
                    }
                  : {}),
                prices: note.lines.map((l: any) => ({
                  lineId: l.id,
                  unitPrice: Number(f.get(`price-${l.id}`)),
                })),
              }),
            );
            if (saved) close();
          }}
        >
          <div className="ph-grid">
            <label>
              Địa chỉ
              <input
                name="address"
                defaultValue={note.customer_snapshot.address || ""}
              />
            </label>
            <label>
              Liên hệ
              <input
                name="contact"
                defaultValue={note.customer_snapshot.contact || ""}
              />
            </label>
            <label>
              Xe / biển số
              <input
                name="vehicle"
                defaultValue={note.customer_snapshot.vehicle || ""}
              />
            </label>
            <label>
              Thuế
              <select
                value={taxMode}
                onChange={(e) => setTaxMode(e.target.value)}
              >
                <option value="keep">Giữ nguyên</option>
                <option value="none">Không thuế (KHD)</option>
                {note.purpose === "CUSTOMER" && (
                  <>
                    <option value="8">8%</option>
                    <option value="10">10%</option>
                    <option value="custom">Tự nhập</option>
                  </>
                )}
              </select>
            </label>
            {taxMode === "custom" && (
              <label>
                Thuế %
                <input
                  name="customTax"
                  type="number"
                  required
                  min="0.001"
                  max="100"
                  step="0.001"
                />
              </label>
            )}
            {note.lines.map((l: any) => (
              <label key={l.id}>
                Đơn giá · {l.product_name}
                <input
                  name={`price-${l.id}`}
                  type="number"
                  required
                  min="0"
                  step="0.001"
                  defaultValue={Number(l.unit_price)}
                />
              </label>
            ))}
          </div>
          <label className="ph-check">
            <input type="checkbox" name="refreshCompany" />
            Áp dụng thông tin công ty mới từ Setting
          </label>
          <label>
            Lý do điều chỉnh
            <textarea
              name="reason"
              autoFocus
              required
              minLength={5}
              maxLength={500}
            />
          </label>
          <div className="ph-toolbar">
            <button className="primary" disabled={action.busy}>
              Lưu điều chỉnh
            </button>
            <button type="button" className="secondary" onClick={close}>
              Đóng
            </button>
          </div>
        </form>
      </Panel>
    </div>
  );
}
function PrintVoucher({ note }: { note: any }) {
  return (
    <article className="ph-print-document">
      <h2>{note.company_snapshot.name}</h2>
      <p>{note.company_snapshot.address}</p>
      <p>MST: {note.company_snapshot.taxId}</p>
      <h1>
        {note.purpose === "CUSTOMER"
          ? "PHIẾU GIAO HÀNG KIÊM PHIẾU XUẤT KHO"
          : "PHIẾU XUẤT TRẢ NHÀ CUNG CẤP"}
      </h1>
      <p>
        {note.number} ·{" "}
        {String(note.business_date).slice(0, 10).split("-").reverse().join("/")}
      </p>
      <p>Đơn vị nhận: {note.customer_name}</p>
      <p>Địa chỉ: {note.customer_snapshot.address}</p>
      <p>Liên hệ: {note.customer_snapshot.contact}</p>
      <table>
        <thead>
          <tr>
            <th>STT</th>
            <th>Mã / lô</th>
            <th>Hàng hóa</th>
            <th>ĐVT</th>
            <th>Số lượng</th>
            <th>Đơn giá</th>
            <th>Thành tiền</th>
          </tr>
        </thead>
        <tbody>
          {note.lines.map((l: any, i: number) => (
            <tr key={l.id}>
              <td>{i + 1}</td>
              <td>{l.product_code}</td>
              <td>{l.product_name}</td>
              <td>{l.unit}</td>
              <td>{n(l.quantity)}</td>
              <td>{n(l.unit_price)}</td>
              <td>{n(l.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>Tổng tiền hàng: {n(note.subtotal)} đ</p>
      {note.tax_rate != null && (
        <p>
          Thuế {n(note.tax_rate)}%: {n(note.tax_amount)} đ
        </p>
      )}
      <h3>Tổng thanh toán: {n(note.total)} đ</h3>
      <p>Bằng chữ: {note.amount_words}</p>
      <div className="ph-signatures">
        <div>
          Người lập phiếu
          <br />
          <br />
          <br />
          {note.issuer_name}
        </div>
        <div>Người giao hàng</div>
        <div>Người nhận hàng</div>
      </div>
    </article>
  );
}
function Stages() {
  const { user } = useAuth();
  const defs = useData<any[]>("/operation-definitions", []),
    machines = useData<any[]>("/machines", []),
    [editing, setEditing] = useState<any>(null);
  const action = useAction(defs.load);
  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const payload = {
      code: f.get("code"),
      name: f.get("name"),
      stageKind: f.get("stageKind"),
      materialBehavior: f.get("materialBehavior"),
      defaultUnit: f.get("stageKind") === "FINAL_OUTPUT" ? "cái" : "công đoạn",
      requiresMachine: f.get("requiresMachine") === "on",
      isActive: f.get("isActive") === "on",
      machineIds: f.getAll("machineIds"),
    };
    if (
      await action.run(() =>
        editing
          ? api(`/operation-definitions/${editing.id}`, {
              method: "PATCH",
              body: JSON.stringify(payload),
            })
          : post("/operation-definitions", payload),
      )
    )
      setEditing(null);
  };
  return (
    <>
      <ErrorBox message={defs.error || action.error} />
      {can(user, "stages.manage") && (
        <Panel title={editing ? "Sửa công đoạn" : "Tạo công đoạn linh hoạt"}>
          <form key={editing?.id || "new"} onSubmit={save}>
            <div className="ph-grid">
              <label>
                Mã loại công đoạn
                <input name="code" required defaultValue={editing?.code} />
              </label>
              <label>
                Tên hiển thị
                <input name="name" required defaultValue={editing?.name} />
              </label>
              <label>
                Ảnh hưởng tiến độ
                <select
                  name="stageKind"
                  defaultValue={editing?.stage_kind || "PREPARATION"}
                >
                  <option value="PREPARATION">Chuẩn bị / bán thành phẩm</option>
                  <option value="FINAL_OUTPUT">
                    Tạo thành phẩm (tính tiến độ)
                  </option>
                  <option value="POST_PRODUCTION">Sau sản xuất</option>
                </select>
              </label>
              <label>
                Nghiệp vụ vật tư
                <select
                  name="materialBehavior"
                  defaultValue={editing?.material_behavior || "NONE"}
                >
                  <option value="NONE">Không chuyển đổi vật tư</option>
                  <option value="SPLIT">Chia · cân từng cuộn đầu ra</option>
                  <option value="PRINT">
                    In trọn cuộn · giữ mã / không cân lại
                  </option>
                  <option value="LAMINATE">Ghép hai cuộn · cân đầu ra</option>
                  <option value="FINAL">
                    Bế / tạo thành phẩm · quản lý phần dư
                  </option>
                </select>
              </label>
              <label className="ph-check">
                <input
                  type="checkbox"
                  name="requiresMachine"
                  defaultChecked={editing?.requires_machine ?? true}
                />
                Dùng máy
              </label>
              <label className="ph-check">
                <input
                  type="checkbox"
                  name="isActive"
                  defaultChecked={editing?.is_active ?? true}
                />
                Cho phép sử dụng
              </label>
            </div>
            <fieldset>
              <legend>Máy có thể làm công đoạn</legend>
              {machines.data.map((m) => (
                <label className="ph-check" key={m.id}>
                  <input
                    name="machineIds"
                    type="checkbox"
                    value={m.id}
                    defaultChecked={editing?.machine_ids?.includes(m.id)}
                  />
                  {m.code} · {m.name}
                </label>
              ))}
            </fieldset>
            <button className="primary" disabled={action.busy}>
              Lưu công đoạn & máy phù hợp
            </button>
            {editing && (
              <button
                type="button"
                className="secondary"
                onClick={() => setEditing(null)}
              >
                Tạo mới
              </button>
            )}
          </form>
        </Panel>
      )}
      <Panel title="Công đoạn đã cấu hình">
        {defs.data.map((d) => (
          <div className="ph-stock-row" key={d.id}>
            <div>
              <b>{d.name}</b>
              <span>
                {d.stage_kind === "FINAL_OUTPUT"
                  ? "Tính tiến độ thành phẩm"
                  : "Không tính miếng thành phẩm"}{" "}
                · {d.material_behavior} · {d.machine_ids.length} máy phù hợp
              </span>
            </div>
            {can(user, "stages.manage") && (
              <button className="secondary" onClick={() => setEditing(d)}>
                Sửa
              </button>
            )}
          </div>
        ))}
      </Panel>
    </>
  );
}
export function Settings() {
  const { user } = useAuth();
  const data = useData<any>("/integrated/settings", null),
    [draft, setDraft] = useState<any>(null);
  const action = useAction(data.load);
  useEffect(() => {
    if (data.data && !draft) setDraft(data.data);
  }, [data.data, draft]);
  if (!draft) return <ErrorBox message={data.error} />;
  return (
    <><Panel title="Cấu hình công ty / mã kho / ghi hàng lỗi">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void action.run(() =>
            api("/integrated/settings", {
              method: "PUT",
              body: JSON.stringify(draft),
            }),
          );
        }}
      >
        <div className="ph-grid">
          {[
            ["name", "Tên công ty"],
            ["address", "Địa chỉ"],
            ["taxId", "Mã số thuế"],
          ].map(([key, label]) => (
            <label key={key}>
              {label}
              <input
                value={draft.company[key!]}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    company: { ...draft.company, [key!]: e.target.value },
                  })
                }
              />
            </label>
          ))}
          {[
            ["RAW", "Prefix nguyên liệu"],
            ["SEMI_FINISHED", "Prefix bán thành phẩm"],
            ["FINISHED", "Prefix thành phẩm"],
          ].map(([key, label]) => (
            <label key={key}>
              {label}
              <input
                maxLength={12}
                value={draft.prefixes[key!]}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    prefixes: {
                      ...draft.prefixes,
                      [key!]: e.target.value.toUpperCase(),
                    },
                  })
                }
              />
            </label>
          ))}
          <label>
            Ghi miếng lỗi
            <select
              value={draft.defectPolicy}
              onChange={(e) =>
                setDraft({ ...draft, defectPolicy: e.target.value })
              }
            >
              <option value="OFF">Tắt</option>
              <option value="OPTIONAL">Tùy chọn (hiện tại)</option>
              <option value="REQUIRED">Bắt buộc, kể cả 0 lỗi</option>
            </select>
          </label>
        </div>
        <p className="ph-hint">
          Đổi mã không đổi mã cuộn/lô cũ. Đổi thông tin công ty không sửa phiếu
          đã phát hành.
        </p>
        {can(user, "settings.manage") && (
          <button className="primary" disabled={action.busy} aria-busy={action.busy}>
            {action.busy ? 'Đang lưu cấu hình…' : 'Lưu cấu hình'}
          </button>
        )}
        <ErrorBox message={action.error} />
      </form>
    </Panel><PrintingAreaPolicySettings /></>
  );
}
